import { FC, useEffect } from 'react'
import { Field, Formik } from 'formik'
import * as Yup from 'yup'
import { useTranslation } from 'next-i18next'
import { observer } from 'mobx-react'
import {
  CurrencyUnit,
  IJobCategoryBase,
  ILocationBase,
  ISkillBase,
  JobTypeOfPosition,
  JobWorkExperience,
  JobWorkingForm,
  TokenGrant,
} from '@/types'
import { KeywordSearchGroup } from '@/components/organisms/KeywordSearchGroup'
import { KeywordSearchGroupForm } from '@/components/molecules/KeywordSearchGroupForm'
import { FormItem } from '@/components/atoms/FormItem'
import { WorkExperienceField } from '@/components/molecules/WorkExperienceField'
import { SkillField } from '@/components/atoms/SkillField'
import { decodeToStringWithoutCommas, getValidTokenGrants } from '@/utils'
import { JobsPrimaryForm, JobsValues } from '@/components/molecules/JobsPrimaryForm'
import { InputFieldGroup } from '@/components/molecules/InputFieldGroup'
import { AmountTextField } from '@/components/molecules/AmountTextField'
import { CurrencyUnitField } from '@/components/atoms/CurrencyUnitField'
import { LocationField } from '@/components/atoms/LocationField'
import styles from './index.module.scss'

export type FetchJobsValues = {
  keyword: string
  unit: CurrencyUnit
  maxSalary: string
  minSalary: string
  token: boolean[]
  position: JobTypeOfPosition[]
  experience: JobWorkExperience
  form: JobWorkingForm[]
  jobCategories: string[]
  skills: string[]
  locations: string[]
}

type Props = {
  search: (input: FetchJobsValues) => Promise<void>
}

type Value = {
  keyword: string
  unit: CurrencyUnit
  maxSalary: string
  minSalary: string
  token: TokenGrant[]
  typeOfPosition: JobTypeOfPosition[]
  workExperience: JobWorkExperience
  workingForm: JobWorkingForm[]
  jobCategories: IJobCategoryBase[]
  skills: ISkillBase[]
  locations: ILocationBase[]
}

export const KeywordSearchGroupForJobs: FC<Props> = observer(({ search }) => {
  const { t } = useTranslation(['jobs', 'search', 'p', 'validation'])

  const initialValue: JobsValues = {
    keyword: '',
    unit: CurrencyUnit.USD,
    maxSalary: '',
    minSalary: '',
    token: [],
    typeOfPosition: [],
    workExperience: undefined,
    workingForm: [],
    jobCategories: [],
    skills: [],
    locations: [],
  }

  const validationSchema = Yup.object().shape({
    minSalary: Yup.number()
      .typeError(t('validation:半角数字で入力してください'))
      .integer(t('validation:正数を入力してください'))
      .moreThan(-1, t('validation:0以上の値を入力してください。'))
      .transform((value, originalValue: string) => {
        const valueWithoutCommas = Number(decodeToStringWithoutCommas(originalValue))
        if (Number.isNaN(valueWithoutCommas)) {
          return originalValue
        }
        return valueWithoutCommas
      }),
    maxSalary: Yup.number()
      .typeError(t('validation:半角数字で入力してください'))
      .integer(t('validation:正数を入力してください'))
      .moreThan(-1, t('validation:0以上の値を入力してください。'))
      .transform((value, originalValue: string) => {
        const valueWithoutCommas = Number(decodeToStringWithoutCommas(originalValue))
        if (Number.isNaN(valueWithoutCommas)) {
          return originalValue
        }
        return valueWithoutCommas
      }),
  })

  return (
    <div className={styles.container}>
      <KeywordSearchGroup>
        <Formik
          initialValues={initialValue}
          validationSchema={validationSchema}
          onSubmit={async (values: Value, actions) => {
            await search({
              keyword: values.keyword,
              unit: values.unit,
              maxSalary: decodeToStringWithoutCommas(values.maxSalary),
              minSalary: decodeToStringWithoutCommas(values.minSalary),
              token: getValidTokenGrants(values.token),
              position: values.typeOfPosition ? values.typeOfPosition : [],
              experience: values.workExperience ? values.workExperience : null,
              form: values.workingForm ? values.workingForm : [],
              jobCategories: values.jobCategories.map((jobCategory) => jobCategory.id),
              skills: values.skills.map((skill) => skill.id),
              locations: values.locations.map((location) => location.id),
            })
            actions.setSubmitting(false)
          }}
        >
          {({ isSubmitting, setFieldValue, values }) => {
            useEffect(() => {
              // 通貨単位が変更されるたびに、平均投資金額をリセット
              setFieldValue('minSalary', '')
              setFieldValue('maxSalary', '')
            }, [values.unit])

            return (
              <KeywordSearchGroupForm
                renderPrimaryForm={() => <JobsPrimaryForm values={values} setFieldValue={setFieldValue} />}
                isSubmitting={isSubmitting}
              >
                <FormItem label={t('p:キーワード検索')}>
                  <Field
                    name='keyword'
                    type='text'
                    fieldType='search'
                    placeholder={t('p:キーワードを入力')}
                    component={InputFieldGroup}
                  />
                </FormItem>
                <FormItem label={t('月収')}>
                  <div className={styles.salary}>
                    <AmountTextField
                      currencyUnit={values.unit}
                      minAmount={values.minSalary}
                      maxAmount={values.maxSalary}
                      minAmountName='minSalary'
                      maxAmountName='maxSalary'
                      setFieldMinValue={(value) => setFieldValue('minSalary', value)}
                      setFieldMaxValue={(value) => setFieldValue('maxSalary', value)}
                    />
                    <div className={styles.currencyUnitField}>
                      <CurrencyUnitField hasUnit />
                    </div>
                  </div>
                </FormItem>
                <FormItem label={t('勤務地')}>
                  <LocationField
                    selectedLocations={values?.locations}
                    setFieldValue={(locations) => setFieldValue('locations', locations)}
                  />
                </FormItem>
                <FormItem label={t('経験年数')}>
                  <WorkExperienceField shouldShowUnselectedOption />
                </FormItem>
                <FormItem label={t('スキル')}>
                  <SkillField
                    selectedSkills={values?.skills}
                    setFieldValue={(skills) => setFieldValue('skills', skills)}
                  />
                </FormItem>
              </KeywordSearchGroupForm>
            )
          }}
        </Formik>
      </KeywordSearchGroup>
    </div>
  )
})
